import axios, { AxiosError } from "axios";

import { clearToken, getToken } from "./auth";

export const api = axios.create({
  baseURL: "/",
  // Don't set a default Content-Type. Axios picks the right one per
  // payload: `application/json` for plain objects, `multipart/form-data;
  // boundary=...` for FormData. Forcing a default here suppresses the
  // FormData auto-detection — uploads then ship as JSON and the backend
  // returns 422 with "field 'file' is missing" because no multipart parts
  // ever arrive.
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string | Record<string, unknown> }>) => {
    if (error.response?.status === 401) {
      clearToken();
      // Don't redirect on auth endpoints themselves — the page handles its own
      // 401 (wrong password) UI. Only redirect when a protected page got 401.
      const url = error.config?.url ?? "";
      if (!url.includes("/api/auth/login") && !url.includes("/api/auth/register")) {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  },
);

export function extractErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") return JSON.stringify(detail);
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
